// src/api/dashboardEditingModes.ts
//
// The two editing modes (plan 2026-10-05-dashboard-version-history, B-S4):
// publish without review, the editing-mode settings, share in place, publish
// a stored version, and the scope review policy ("Dashboards shared here
// require review"). The types are the generated ones (`make generate-types`).
//
// WHY A MODULE OF ITS OWN, as `dashboardVersions.ts`: the page harnesses mock
// `dashboards.ts` wholesale. The dashboard writes go through the shared
// request wrappers of `dashboardMutations.ts` (ADR-0228), so the page
// refreshes the row's revision and review state after a publish, a settings
// change or a share exactly as after any other write. The policy routes are
// not under `/dashboards/...` and change no dashboard, so they use the plain
// client.
import { apiJson } from "./dashboardMutations";
import { apiJson as clientJson, ApiError } from "./client";
import type { components } from "@verity/shared-types";

export type DashboardEditingSettings = components["schemas"]["DashboardEditingSettingsDTO"];
export type DashboardShareRequest = components["schemas"]["DashboardShareRequest"];
export type DashboardTransition = components["schemas"]["DashboardTransitionResponse"];
export type DashboardReviewPolicy = components["schemas"]["DashboardReviewPolicyDTO"];
export type DraftChangesChoice = "publish_first" | "discard";

function dash(dashboardId: string): string {
  return `/dashboards/${encodeURIComponent(dashboardId)}`;
}

/** Publish the draft without review (publishing mode, review off).
 *  `revision` is the draft's revision the caller saw. */
export async function publishDashboard(
  dashboardId: string,
  revision: number,
  payload: { note?: string } = {}
): Promise<DashboardTransition> {
  return apiJson<DashboardTransition>(`${dash(dashboardId)}/publish`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: { "If-Match": String(revision) },
  });
}

/** Change the editing mode. Accepts the draft: the server writes its
 *  published row. Turning publishing off while the draft has unpublished
 *  changes answers 409 until `draft_changes` says what to do with them
 *  (see `isUnpublishedChangesConflict`). */
export async function updateDashboardSettings(
  dashboardId: string,
  revision: number,
  payload: {
    publishing_enabled?: boolean;
    require_review?: boolean;
    draft_changes?: DraftChangesChoice;
  }
): Promise<DashboardEditingSettings> {
  return apiJson<DashboardEditingSettings>(`${dash(dashboardId)}/settings`, {
    method: "PUT",
    body: JSON.stringify(payload),
    headers: { "If-Match": String(revision) },
  });
}

/** Share a personal dashboard into a workspace or the whole account, in
 *  place. `keep_history: false` deletes its earlier versions. */
export async function shareDashboard(
  dashboardId: string,
  revision: number,
  payload: Omit<DashboardShareRequest, "if_match">
): Promise<DashboardTransition> {
  return apiJson<DashboardTransition>(`${dash(dashboardId)}/share`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: { "If-Match": String(revision) },
  });
}

/** Put a stored version live on the published row; the draft does not
 *  change. `revision` is the draft's. */
export async function publishDashboardVersion(
  dashboardId: string,
  versionNumber: number,
  revision: number
): Promise<DashboardTransition> {
  return apiJson<DashboardTransition>(`${dash(dashboardId)}/versions/${versionNumber}/publish`, {
    method: "POST",
    body: JSON.stringify({}),
    headers: { "If-Match": String(revision) },
  });
}

/** The scope a review policy belongs to: the account, or one workspace
 *  (its public `ws_` id). */
export type ReviewPolicyScope = { scope: "account" } | { scope: "workspace"; workspaceId: string };

function policyPath(target: ReviewPolicyScope): string {
  return target.scope === "account"
    ? "/accounts/me/dashboard-review-policy"
    : `/workspaces/${encodeURIComponent(target.workspaceId)}/dashboard-review-policy`;
}

export async function getDashboardReviewPolicy(target: ReviewPolicyScope): Promise<DashboardReviewPolicy> {
  return clientJson<DashboardReviewPolicy>(policyPath(target));
}

export async function setDashboardReviewPolicy(
  target: ReviewPolicyScope,
  requireReview: boolean
): Promise<DashboardReviewPolicy> {
  return clientJson<DashboardReviewPolicy>(policyPath(target), {
    method: "PUT",
    body: JSON.stringify({ require_review: requireReview }),
  });
}

/** The settings route's 409 that asks what to do with the draft's
 *  unpublished changes (as opposed to a stale revision or the policy). */
export function isUnpublishedChangesConflict(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 409) return false;
  const text = `${err.message} ${typeof err.detail === "string" ? err.detail : ""}`;
  return /unpublished changes/i.test(text);
}
